import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { UserService } from '@6grain/masofa-monolith-api-client/identity';
import { UserIndividualDialogComponent } from '../user-individual-dialog/user-individual-dialog.component';
import { UserLegalDialogComponent } from '../user-legal-dialog/user-legal-dialog.component';
import { UserManagementService } from '../user-management.service';

@Component({
  selector: 'app-user-management-modals',
  standalone: true,
  imports: [CommonModule, TranslateModule, ToastModule, UserIndividualDialogComponent, UserLegalDialogComponent],
  providers: [],
  templateUrl: './user-management-modals.component.html',
})
export class UserManagementModalsComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild('individualFileInput') individualFileInput!: ElementRef;
  @ViewChild('legalFileInput') legalFileInput!: ElementRef;

  @Input() rolesData: any[] = [];

  @Output() onUserSaved = new EventEmitter<void>();

  showIndividualDialog: boolean = false;
  showLegalEntityDialog: boolean = false;

  editingUserId: string | null = null;
  lastCreatedUserId: string | null = null;

  currentIndividualData: any = null;
  currentLegalData: any = null;

  individualFileName: string = '';
  legalFileName: string = '';

  isLoading: boolean = false;
  isSave: boolean = false;
  isSend: boolean = false;

  constructor(
    private userService: UserService,
    private userManagementService: UserManagementService,
    private messageService: MessageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  openAddIndividual(): void {
    this.editingUserId = null;
    this.currentIndividualData = null;
    this.showIndividualDialog = true;
  }

  openAddLegal(): void {
    this.editingUserId = null;
    this.currentLegalData = null;
    this.showLegalEntityDialog = true;
  }

  openEdit(user: any): void {
    this.editingUserId = user.id;
    this.isLoading = true;

    this.userManagementService.getCustomUserById(user.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        const profileData = response.profileData || {};
        const userDetails = {
          ...response,
          ...profileData,
          comment: response.comment,
          parentId: response.parentId,
          deputyId: response.deputyId,
          connectionBasis: response.connectionBasis,
          organizationId: response.organizationId,
          position: response.position || profileData.position,
        };

        if (profileData.phones) {
          userDetails.phoneNumber = profileData.phones;
        }

        if (response.userBusinessType === 1) {
          if (profileData.pinfl) {
            userDetails.inn = profileData.pinfl;
          }
        }

        if (response.userBusinessType === 0) {
          if (profileData.inn) {
            userDetails.inn = profileData.inn;
          }
        }

        if (response.userBusinessType === 1) {
          this.currentIndividualData = userDetails;
          this.showIndividualDialog = true;
        } else {
          this.currentLegalData = userDetails;
          this.showLegalEntityDialog = true;
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading user details:', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('ERROR'),
          detail: this.translate.instant('FAILED_LOAD_USER_DATA'),
        });

        this.isLoading = false;
        this.currentLegalData = null;
        this.currentIndividualData = null;
        this.cdr.detectChanges();
      },
    });
  }

  importIndividual(): void {
    this.individualFileInput.nativeElement.click();
  }

  importLegal(): void {
    this.legalFileInput.nativeElement.click();
  }

  onSaveIndividual(formData: any): void {
    this.isSave = true;
    const roles = this.rolesData.filter((role) => formData.selectedRoles[role.id]).map((role) => role.name);

    const userData: any = {
      userName: formData.login,
      email: formData.email,
      roles: roles,
      firstName: formData.firstName,
      lastName: formData.lastName,
      secondName: formData.secondName,
      userBusinessId: formData.organization,
      position: formData.position,
      inn: formData.inn,
      phoneNumber: formData.phoneNumber,
      approved: formData.approved,
      emailConfirmed: formData.emailConfirmed,
      userBusinessType: 1,
      comment: formData.comment || '',
      deputyId: formData.deputyId || null,
      connectionBasis: formData.connectionBasis || '',
      parentId: formData.parentId || null,
      lockUser: formData.isBlocked || false,
      lockoutStart:
        formData.isBlocked && formData.blockoutStartDate ? this.formatDateForApi(formData.blockoutStartDate) : null,
      lockoutEnd:
        formData.isBlocked && formData.blockoutEndDate ? this.formatDateForApi(formData.blockoutEndDate) : null,
    };

    if (formData.password) {
      if (this.editingUserId) {
        userData.newPassword = formData.password;
        userData.confirmPassword = formData.confirmPassword;
      } else {
        userData.password = formData.password;
        userData.confirmPassword = formData.confirmPassword;
      }
    }

    if (this.editingUserId) {
      userData.id = this.editingUserId;
    }

    this.sendSaveRequest(userData, () => {
      this.showIndividualDialog = false;
      this.individualFileName = '';
    });
  }

  onSaveLegal(formData: any): void {
    this.isSave = true;
    const roles = this.rolesData.filter((role) => formData.selectedRoles[role.id]).map((role) => role.name);
    const userData: any = {
      userName: formData.login,
      email: formData.email,
      roles: roles,
      shortName: formData.shortName,
      fullName: formData.fullName,
      inn: formData.inn,
      phoneNumber: formData.phone,
      approved: formData.approved,
      emailConfirmed: formData.emailConfirmed,
      userBusinessType: 0, // Firm = 0
      comment: formData.comment || '',
      deputyId: formData.deputyId || null,
      connectionBasis: formData.connectionBasis || '',
      parentId: formData.parentId || null,
      lockUser: formData.isBlocked || false,
      lockoutStart:
        formData.isBlocked && formData.blockoutStartDate ? this.formatDateForApi(formData.blockoutStartDate) : null,
      lockoutEnd:
        formData.isBlocked && formData.blockoutEndDate ? this.formatDateForApi(formData.blockoutEndDate) : null,
    };

    if (formData.password) {
      if (this.editingUserId) {
        userData.newPassword = formData.password;
        userData.confirmPassword = formData.confirmPassword;
      } else {
        userData.password = formData.password;
        userData.confirmPassword = formData.confirmPassword;
      }
    }

    if (this.editingUserId) {
      userData.id = this.editingUserId;
    }

    this.sendSaveRequest(userData, () => {
      this.showLegalEntityDialog = false;
      this.legalFileName = '';
    });
  }

  private sendSaveRequest(userData: any, onSuccessCleanup: () => void): void {
    const request$ = this.editingUserId
      ? this.userService.identityUserCustomUpdatePut(userData)
      : this.userService.identityUserCustomCreatePost(userData);

    request$.subscribe({
      next: (response) => {
        if (response && response.errors && response.errors.length > 0) {
          this.isSave = false;
          const errorMessages = response.errors.map((e: any) => e.description || e.code).join('; ');
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('VALIDATION_ERROR'),
            detail: errorMessages,
          });
          this.cdr.detectChanges();
          return;
        }

        if (!this.editingUserId && response && response.id) {
          this.lastCreatedUserId = response.id;
        }

        this.messageService.add({
          severity: 'success',
          summary: this.editingUserId
            ? this.translate.instant('SUCCESS')
            : this.translate.instant('USER_CREATED_TITLE'),
          detail: this.editingUserId
            ? this.translate.instant('USER_UPDATED_SUCCESSFULLY')
            : this.translate.instant('USER_CREATED_SUCCESSFULLY'),
        });

        this.isSave = false;
        this.editingUserId = null;
        onSuccessCleanup();

        this.onUserSaved.emit();
      },
      error: (error) => {
        this.isSave = false;
        const rawMessage = error?.error?.message || error?.message || 'Failed to create/update user';
        const translated = this.translate.instant(rawMessage);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('Ошибка'),
          detail: translated || rawMessage,
        });
        this.cdr.detectChanges();
      },
    });
  }

  sendNewAccountLetter(type: 'individual' | 'legal'): void {
    this.isSend = true;
    const userId = this.editingUserId || this.lastCreatedUserId;

    if (!userId) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('Warning'),
        detail: this.translate.instant('PLEASE_SAVE_USER_FIRST'),
      });
      this.isSend = false;
      return;
    }

    this.userService.identityUserResendNewAccountLetterIdPost(userId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SUCCESS'),
          detail: this.translate.instant('LETTER_SENT_SUCCESSFULLY'),
        });
        this.isSend = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error.error?.message || 'Failed' });
        this.isSend = false;
        this.cdr.detectChanges();
      },
    });
  }

  onIndividualFileSelect(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.individualFileName = file.name;
      this.processImportFile(file, 'individual');
    }
  }

  onLegalFileSelect(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.legalFileName = file.name;
      this.processImportFile(file, 'legal');
    }
  }

  private processImportFile(file: File, type: 'individual' | 'legal'): void {
    const request$ =
      type === 'individual'
        ? this.userService.identityUserImportUserPersonPost(file)
        : this.userService.identityUserImportUserFirmPost(file);

    request$.subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('SUCCESS'),
          detail: this.translate.instant('IMPORT_SUCCESSFUL'),
        });
        this.onUserSaved.emit();
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.error?.message || 'Import failed',
        });
        this.cdr.detectChanges();
      },
    });
  }

  private formatDateForApi(date: Date | string): string {
    const dateObj = date instanceof Date ? date : new Date(date);
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    const seconds = String(dateObj.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
